import { useCallback, useMemo, useRef } from "react"

import { Line } from "react-chartjs-2"
import { useSelector } from "react-redux"

import { useIncognito } from "../../../../hooks/useIncognito"
import { tooltipLineWithPoints } from "../../../DashboardPage/components/chartsPlagins"
import { datasetsOptions, getUsageChartOptions } from "./chartOptions"
import UsageOverviewTooltip from "./UsageOverviewTooltip"

const PREV_PREFIX = {
  "previous month": "Prev. month",
  "previous year": "Prev. year",
  "previous week": "Prev. week",
  "previous period": "Prev. period"
}
const getPrevPrefix = (label) =>
  !label ? "Prev." : PREV_PREFIX[label.trim().toLowerCase()] || "Prev."

const UsageOverviewChart = ({ data }) => {
  const { theme } = useSelector((state) => state.nav)
  const incognito = useIncognito()
  const tooltipRef = useRef()

  const setTooltipImperative = useCallback((p) => {
    tooltipRef.current?.update(p)
  }, [])

  const chartOptions = useMemo(
    () => getUsageChartOptions(theme, setTooltipImperative, incognito),
    [theme, incognito]
  )

  const prevPrefix = getPrevPrefix(data?.comparison_period?.label)

  const chartData = useMemo(() => {
    if (!data?.labels?.length) return null

    const mainLabels = data.labels
    const compLabels = data.comparison?.labels ?? []
    const hasComparison = compLabels.length > 0

    const mainSet = new Set(mainLabels)
    const hasOverlap = hasComparison && compLabels.some((l) => mainSet.has(l))

    const mapAtLabels = (targetLabels, srcLabels, srcArr) => {
      if (!srcArr) return srcArr
      const map = new Map(srcLabels.map((l, i) => [l, srcArr[i]]))
      return targetLabels.map((l) => (map.has(l) ? map.get(l) : null))
    }

    let labels
    let mainLabelsAligned
    let comparisonLabels
    let getMain
    let getComp

    if (hasOverlap) {
      // align by calendar date — union of labels
      labels = Array.from(new Set([...mainLabels, ...compLabels])).sort()
      mainLabelsAligned = labels.map((l) => (mainSet.has(l) ? l : null))
      comparisonLabels = labels
      getMain = (key) => mapAtLabels(labels, mainLabels, data[key])
      getComp = (key) => mapAtLabels(labels, compLabels, data.comparison?.[key])
    } else if (hasComparison) {
      // index-in-index — X-axis follows the longer series so nothing gets cut off
      const compLonger = compLabels.length > mainLabels.length
      labels = compLonger ? compLabels : mainLabels
      mainLabelsAligned = labels.map((_, i) => mainLabels[i] ?? null)
      comparisonLabels = labels.map((_, i) => compLabels[i] ?? null)
      getMain = (key) => data[key] ?? []
      getComp = (key) => data.comparison?.[key] ?? []
    } else {
      labels = mainLabels
      mainLabelsAligned = labels
      comparisonLabels = labels.map(() => null)
      getMain = (key) => data[key] ?? []
      getComp = () => []
    }

    const pointDatasetOptions =
      labels.length === 1
        ? { pointHoverRadius: 6, pointRadius: 4 }
        : { pointRadius: 0, pointHoverRadius: 0 }

    const comparisonOverlayOptions = {
      borderDash: [8, 6],
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 0
    }

    const datasets = []
    const pushSeries = (opts, key) => {
      datasets.push({
        ...opts,
        ...pointDatasetOptions,
        data: (getMain(key) ?? []).map((v) => (v == null ? null : Number(v))),
        mainLabels: mainLabelsAligned
      })
      if (hasComparison) {
        datasets.push({
          ...opts,
          ...comparisonOverlayOptions,
          label: `${prevPrefix} ${opts.label}`,
          datasetType: `${opts.datasetType}_previous`,
          data: (getComp(key) ?? []).map((v) => (v == null ? null : Number(v))),
          comparisonLabels
        })
      }
    }

    pushSeries(datasetsOptions.prevent, "prevent")
    pushSeries(datasetsOptions.deflect, "deflect")

    return { labels, datasets }
  }, [data, prevPrefix])

  return (
    <div className="billing-usage-overview-chart">
      <UsageOverviewTooltip ref={tooltipRef} />
      <div style={{ paddingBottom: 22 }}>
        <Line
          style={{ height: 320 }}
          data={chartData ?? { labels: [], datasets: [] }}
          options={chartOptions}
          plugins={[tooltipLineWithPoints]}
        />
      </div>
    </div>
  )
}

export default UsageOverviewChart
